import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import Breadcrumbs from '../components/Breadcrumbs';
import BreadcrumbsJsonLd from '../components/BreadcrumbsJsonLd';

export default function ForCorporates() {
  useSEO({
    title: 'Enterprise AI Assessment Platform | GreenTick',
    description: 'Stop gut-feel hiring. GreenTick empowers enterprise talent teams with AI-native behavioral capture and skill verification assessments. Hire 10X faster with verified data.',
    canonicalPath: '/for-corporates',
  });
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      <BreadcrumbsJsonLd 
        items={[
          { name: 'Home', item: '/' },
          { name: 'For Corporates', item: '/for-corporates' }
        ]}
      />

      <div className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumbs 
              items={[{ name: 'For Corporates', item: '/for-corporates' }]} 
            />

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Accelerate Hiring,
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                Empower Teams
              </span>
            </h1>

            <p className="text-xl text-slate-300 mb-12 leading-relaxed">
              Transform your corporate hiring process with AI-powered screening rounds, automated feedback, and data-driven recruitment insights.
            </p>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-teal-400" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-3">Intelligent Screening</h2>
                <p className="text-slate-300">
                  AI-driven platform evaluates resumes, conducts preliminary interviews, and screens candidates with remarkable precision. Say goodbye to tedious manual screenings and hello to intelligent automation.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-3">Automated Filtering</h2>
                <p className="text-slate-300">
                  Uses advanced algorithms to scan resumes and match them against job descriptions, filtering out unqualified candidates efficiently.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-teal-400" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-3">Bias Reduction</h2>
                <p className="text-slate-300">
                  Removes unconscious bias from the initial screening process by focusing solely on qualifications and experience.
                </p>
              </div>

              <div className="glass p-8 rounded-2xl">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                </div>
                <h2 className="text-xl font-semibold text-white mb-3">Data-Driven Insights</h2>
                <p className="text-slate-300">
                  Leverage the power of data to make informed hiring decisions with actionable insights and analytics.
                </p>
              </div>
            </div>

            <div className="glass p-12 rounded-2xl mb-12">
              <h2 className="text-3xl font-bold text-white mb-8">What's Included</h2>
              <div className="space-y-4">
                {[
                  'AI-Powered Screening Rounds',
                  'Automated Feedback',
                  'Smart Interview Scheduling',
                  'Data-Driven Recruitment',
                  'Dedicated Support',
                  'Candidate Pipeline Metrics',
                  'Source Effectiveness Analysis'
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-teal-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-lg">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://dashboard.greentick.org.in/auth"
                target="_blank"
                rel="noopener noreferrer"
                className="group px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-2xl hover:shadow-teal-500/50 transition-all hover:scale-105 flex items-center gap-2 justify-center"
              >
                Get Started
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="px-8 py-4 glass text-white rounded-xl font-semibold hover:bg-white/20 transition-all hover:scale-105 flex items-center justify-center"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
